import { PaymentTypeLabel } from '../types';
import { PaymentSession, SyncData } from '../models';
import { Money } from '../utilities';
import { I18n, Store } from '../wrappers';
import { diContainer } from '../DIContainer';
import apiService from './ApiService';
import { CARD_PAYMENT_PROVIDERS } from './CardPaymentProviders';
import moment from 'moment-timezone';
const LOG_PREFIX = '[CardPayment]';
/** Hours after the last successful Kraj dana at which its reminder starts. */
const SETTLEMENT_REMINDER_HOURS = 22;
const CardReadService = {
    currentProvider: null,
    /**
     * The card app this device talks to: the native plugin unless the shell
     * registered another transport under `cardTerminal` (the E2E suite's HTTP
     * double). The same object answers whether a terminal exists at all.
     */
    terminal() {
        return diContainer.resolve('cardTerminal');
    },
    hasTerminal() {
        return this.terminal().available();
    },
    /**
     * Detect which payment provider is configured and enabled
     */
    detectProvider() {
        const tenant = Store.get('tenant');
        const cardPayments = tenant?.venue?.settings?.integrations?.card_payments;
        if (cardPayments?.nlb?.enabled && cardPayments?.nlb?.merchant_unique_id && cardPayments?.nlb?.pin) {
            return 'nlb';
        }
        if (cardPayments?.payoneos?.enabled && cardPayments?.payoneos?.user_hash) {
            return 'payoneos';
        }
        if (cardPayments?.payten?.enabled) {
            return 'payten';
        }
        return null;
    },
    /**
     * Get provider-specific configuration
     * To add a new provider: add a case here and update PaymentProvider type
     */
    getProviderConfig(provider) {
        const tenant = Store.get('tenant');
        const cardPayments = tenant?.venue?.settings?.integrations?.card_payments;
        switch (provider) {
            case 'payoneos':
                if (!cardPayments?.payoneos)
                    return null;
                return {
                    privateKey: cardPayments.payoneos.private_key,
                    softPosUrl: cardPayments.payoneos.softpos_url
                };
            case 'nlb':
                if (!cardPayments?.nlb)
                    return null;
                return {
                    pin: cardPayments.nlb.pin,
                    merchantUniqueID: cardPayments.nlb.merchant_unique_id
                };
            case 'payten':
                return {};
            default:
                return null;
        }
    },
    /**
     * Initialize the payment provider
     */
    async initializeProvider() {
        if (this.currentProvider)
            return;
        const provider = this.detectProvider();
        if (!provider)
            return;
        const config = this.getProviderConfig(provider);
        if (!config)
            return;
        console.log(`${LOG_PREFIX} Initializing provider: ${provider}`);
        await this.terminal().initializeProvider({ provider, config });
        this.currentProvider = provider;
        console.log(`${LOG_PREFIX} Provider initialized: ${provider}`);
    },
    shouldReadCard(order) {
        const provider = this.detectProvider();
        const isCreditCardPayment = this.getCardAmount(order) > 0;
        return !!provider && this.hasTerminal() && isCreditCardPayment;
    },
    /**
     * The configured provider's terminal has an end-of-day settlement ("Kraj dana") and
     * is reachable from this device — what shows the Working Day block and its reminder.
     * No separate opt-in flag: configuring Payten is enough (owner ruling, 2026-09-21).
     */
    hasSettlement() {
        const provider = this.detectProvider();
        return !!provider && CARD_PAYMENT_PROVIDERS[provider].supportsSettlement && this.hasTerminal();
    },
    /**
     * Kraj dana can start now: no card sale or cancel-void is waiting on the terminal
     * (`pendingCardPayment`), since the terminal handles one request at a time. The
     * native provider refuses the overlap too; this keeps the cashier from confirming
     * a request that would be refused. Connectivity is the caller's live UI gate.
     */
    canCloseWorkingDay() {
        return this.hasSettlement() && !Store.get('pendingCardPayment');
    },
    /**
     * Sends Kraj dana to the terminal. A one-way action: receipts inside the settled
     * batch can no longer be voided on this terminal afterward, so the caller confirms
     * with the cashier first. Resolves once the request is sent and throws if it cannot
     * be; the terminal's answer (or the provider giving up on it) arrives through the
     * terminal's workingDayClosed listener. Only the Working Day screen calls this —
     * never the card-sale or cancel-refund flow.
     */
    async startCloseWorkingDay() {
        if (!this.hasSettlement())
            return { success: false, error: 'no_provider_configured' };
        if (!this.canCloseWorkingDay())
            return { success: false, error: 'request_already_pending' };
        await this.initializeProvider();
        await this.terminal().closeWorkingDay(this.getLocaleCurrency());
        return { success: true };
    },
    /** The terminal answered a Kraj dana with success: the reminder starts over. */
    recordSettlement() {
        Store.set('cardSettlement', { lastSettledAt: moment().toISOString(), unsettledPayments: false });
    },
    /**
     * Kraj dana is overdue on this device: a card payment was taken here since the last
     * successful one, and that one answered more than SETTLEMENT_REMINDER_HOURS ago or
     * never ran here. The terminal needs one at least every 24 hours; the reminder
     * starts two hours before that.
     */
    isSettlementOverdue(now = moment()) {
        if (!this.hasSettlement())
            return false;
        const { lastSettledAt, unsettledPayments } = Store.get('cardSettlement');
        if (!unsettledPayments)
            return false;
        return !lastSettledAt || now.diff(moment(lastSettledAt), 'hours', true) > SETTLEMENT_REMINDER_HOURS;
    },
    async startCardRead(order, printOptions = {}) {
        order = order?.$duplicate();
        const cardAmount = this.getCardAmount(order);
        console.log(`${LOG_PREFIX} startCardRead | order=${order?.hash} amount=${cardAmount} provider=${this.currentProvider || 'none'}`);
        if (!this.shouldReadCard(order)) {
            console.error(`${LOG_PREFIX} Card read not allowed`);
            return { success: false, error: 'activation_not_found' };
        }
        await this.initializeProvider();
        if (!this.currentProvider) {
            console.error(`${LOG_PREFIX} No provider configured`);
            return { success: false, error: 'no_provider_configured' };
        }
        return this.startPayment(order, cardAmount, printOptions);
    },
    /**
     * Start payment with the current provider. Where the session row is created
     * first — by the backend (needs connectivity) or on the device (synced later)
     * — is the provider's capability, not its name.
     */
    async startPayment(order, amount, printOptions) {
        if (CARD_PAYMENT_PROVIDERS[this.currentProvider].sessionStrategy === 'local') {
            return this.startLocalSessionPayment(order, amount, printOptions);
        }
        return this.startServerSessionPayment(order, amount, printOptions);
    },
    /**
     * A device-created session row mirroring the request sent to the terminal;
     * `extra` carries the reversal references or the recreate marker.
     */
    buildLocalSession(provider, order, type, amountStr, extra = {}, hash) {
        return new PaymentSession({
            ...(hash ? { hash } : {}),
            order_hash: order.hash,
            provider,
            amount: amountStr,
            transaction_type: type,
            transaction_class: 'POS',
            status: 'pending',
            request_data: { order_hash: order.hash, amount: amountStr, type, currency_code: this.getLocaleCurrency(), ...extra },
            date: moment().format('YYYY-MM-DD'),
        });
    },
    async startLocalSessionPayment(order, amount, printOptions) {
        const provider = this.currentProvider;
        const amountStr = Money.parse(amount).toFormat('0.0000');
        const currencyCode = this.getLocaleCurrency();
        console.log(`${LOG_PREFIX} [${provider}] Creating local session | order=${order.hash} amount=${amountStr}`);
        const session = this.buildLocalSession(provider, order, PaymentSession.SALE, amountStr);
        // Durable and queued before the terminal is invoked, and announced to peer
        // terminals so their guards see the charge in flight. Nothing is pushed to the
        // server yet: it first hears of the session together with its outcome, so a
        // late answer to a pending row can never race the terminal's answer.
        await session.queue('create');
        session.announce();
        console.log(`${LOG_PREFIX} [${provider}] Local session created | sessionId=${session.hash}`);
        Store.set('pendingCardPayment', {
            provider,
            paymentSessionId: session.hash,
            order,
            printOptions
        });
        const cardReadParams = {
            provider,
            paymentSessionId: session.hash,
            amount: Money.parse(amount).toFormat('0.00'),
            currencyCode,
        };
        console.log(`${LOG_PREFIX} [${provider}] Starting card read`);
        this.terminal().startCardRead(cardReadParams);
        return { success: true };
    },
    async startServerSessionPayment(order, amount, printOptions) {
        const provider = this.currentProvider;
        const txType = order.getToPay().toUnit() > 0 ? 'Sale' : 'Refund';
        console.log(`${LOG_PREFIX} [${provider}] Creating session | order=${order.hash} amount=${amount} type=${txType}`);
        let response;
        try {
            response = await apiService.post('/integrations/payments/create-session', {
                order_hash: order.hash,
                amount: Money.parse(amount).toFormat('0.0000'),
                type: txType
            });
        }
        catch (error) {
            console.error(`${LOG_PREFIX} [${provider}] Session creation request failed: ${error?.message}`);
            return { success: false, error: 'network_unavailable' };
        }
        if (!response.data.success) {
            console.error(`${LOG_PREFIX} [${provider}] Session creation failed: ${response.data.error}`);
            return { success: false, error: response.data.error };
        }
        const paymentSessionId = String(response.data.session_id);
        console.log(`${LOG_PREFIX} [${provider}] Session created | sessionId=${paymentSessionId}`);
        Store.set('pendingCardPayment', {
            provider,
            paymentSessionId,
            order,
            printOptions
        });
        const cardReadParams = {
            provider,
            paymentSessionId,
            amount: Money.parse(amount).toFormat('0.00'),
            paymentSessionToken: response.data.payment_session_token || undefined,
            // paymentRefundIdentificator - Required for NLB refund transactions. Retrieve from original order's payment session when implementing refunds.
        };
        console.log(`${LOG_PREFIX} [${provider}] Starting card read`);
        this.terminal().startCardRead(cardReadParams);
        return { success: true };
    },
    /**
     * Record the terminal's answer on the session the payment started with.
     * Bookkeeping only: the caller isolates it so a failure never blocks the
     * receipt for a card that was charged, or the storno for an approved void.
     */
    async finalizeSession(result, pending) {
        if (CARD_PAYMENT_PROVIDERS[pending.provider].sessionStrategy === 'local') {
            return this.finalizeLocalSession(result, pending);
        }
        return this.finalizeServerSession(result, pending);
    },
    /** Server-session providers: the backend owns the row and only needs the result the card app returned. */
    async finalizeServerSession(result, pending) {
        if (!result.receiptData)
            return;
        await apiService.post('/integrations/payments/finalize', {
            provider: pending.provider,
            session_id: pending.paymentSessionId,
            success: result.success,
            status_code: result.statusCode,
            message: result.message,
            receipt_data: result.receiptData,
            order_hash: pending.order.hash
        });
    },
    /**
     * Record the terminal result on the locally-created session and queue it for sync.
     * Total by design: every result — including no-receipt errors (broadcast_failed,
     * empty_response_data, response_parse_error, ecr_not_allowed) — finalizes the
     * session, so no row is ever left 'pending' forever.
     */
    async finalizeLocalSession(result, pending) {
        let session = PaymentSession.find(pending.paymentSessionId);
        if (!session) {
            // The local record can be GC'd if a sync drain ran while the payment
            // was in flight (id-less records are dropped on server rejection).
            // Recreate it from the pending context so the result is never lost.
            console.warn(`${LOG_PREFIX} finalizeLocalSession: session missing, recreating | hash=${pending.paymentSessionId}`);
            const order = pending.order;
            const isVoid = pending.intent === 'cancelRefund';
            // A void reverses the sale session's exact amount and keeps its reference;
            // only a sale derives the amount from the order.
            const reference = isVoid ? this.saleReference(this.completedSales(order, pending.provider)[0]) : null;
            const amountStr = reference ? reference.saleAmount : Money.parse(this.getCardAmount(order)).toFormat('0.0000');
            session = this.buildLocalSession(pending.provider, order, isVoid ? PaymentSession.VOID : PaymentSession.SALE, amountStr, {
                recreated: true,
                ...(reference ? { refund_of: reference.saleSessionHash, refund_reference: reference.reference } : {}),
            }, pending.paymentSessionId);
        }
        session.status = result.success ? 'completed' : 'failed';
        session.response_data = {
            status_code: result.statusCode ?? null,
            message: result.message ?? null,
            receipt_data: result.receiptData ?? null,
            // What the provider hands back to reverse this transaction later (empty on a failure).
            refund_reference: result.paymentRefundIdentificator || null,
            error: result.error ?? null,
        };
        session.finalized_at = moment().utc().format(PaymentSession.DATE_FORMAT);
        // The terminal now holds a transaction its next Kraj dana must settle.
        if (result.success && CARD_PAYMENT_PROVIDERS[pending.provider].supportsSettlement) {
            Store.set('cardSettlement', { ...Store.get('cardSettlement'), unsettledPayments: true });
        }
        const queued = await session.queue('update');
        void session.push(queued);
    },
    /**
     * Cancel-reversal gate: the configured provider can reverse a sale, the venue
     * opted in (integrations.card_payments.<provider>.refund_on_cancel — default
     * off until the flow is verified on a live terminal) and the order carries a
     * positive card amount. E-invoice stornos keep their own (online-only) branch.
     *
     * Also requires a sale session for this order to exist SOMEWHERE this device
     * can see: the live local store (own write or a peer's syncTerminals
     * broadcast), or — failing that — the sync-data backup store's trace of a
     * session this device itself finalized but whose live row the drain's id-less
     * GC later dropped (the D40 race). A card-paid order with neither either never
     * went through Payten (shouldReadCard's prompt was declined) or was charged on
     * a different device — and product policy (owner ruling, 2026-09-15) does not
     * require automatic cross-device reversal for that case: the cashier stornos
     * the Konty receipt on this device and reverses the card charge separately, on
     * whatever device/terminal actually took it, which need not have gone through
     * Payten at all. The backup check keeps that ruling from also swallowing a
     * same-device charge the drain merely lost track of — that case still reaches
     * startCancelRefund and blocks on refund_reference_missing exactly as before
     * this gate existed, rather than silently skipping the reversal it needs.
     */
    async shouldRefundOnCancel(order) {
        const provider = this.detectProvider();
        if (!provider || !CARD_PAYMENT_PROVIDERS[provider].supportsCancelReversal)
            return false;
        const cardPayments = Store.get('tenant')?.venue?.settings?.integrations?.card_payments;
        if (!cardPayments?.[provider]?.refund_on_cancel
            || !this.hasTerminal()
            || order.isEinvoice()
            // Same predicate the sale used (shouldReadCard) — getPaidCreditCard() alone
            // is 0 for business-card orders, which silently skipped the reversal.
            || this.getCardAmount(order) <= 0)
            return false;
        if (this.possibleCharges(order, provider).length > 0)
            return true;
        return this.hasBackedUpSale(order, provider);
    },
    /**
     * The sync-data backup store's trace of a Sale session this device
     * finalized for this order+provider, even though the live PaymentSession
     * row is gone — the manual-recovery artifact the D40 drain-wipe race
     * leaves behind (CardPaymentIntegration.md, "Backup copy and the drain
     * wipe"). Presence alone is enough: the gate only needs to know a charge
     * was attempted here, not its outcome — startCancelRefund's own
     * saleReference check handles the rest once routed there.
     */
    async hasBackedUpSale(order, provider) {
        const backup = await SyncData.getDataForSync(true);
        return backup.some(sd => sd.type === 'PaymentSession' && sd.data.some((record) => record.order_hash === order.hash && record.provider === provider && record.transaction_type === PaymentSession.SALE));
    },
    /** The order's sessions with one provider (the configured one unless given) — a reversal must go to the provider that charged the card. */
    sessionsFor(order, provider) {
        const owner = provider ?? this.detectProvider();
        if (!owner)
            return [];
        return PaymentSession.all().filter(s => s.order_hash === order.hash && s.provider === owner);
    },
    /** Completed card charges recorded for the order. */
    completedSales(order, provider) {
        return this.sessionsFor(order, provider).filter(s => s.transaction_type === PaymentSession.SALE && s.status === 'completed');
    },
    /**
     * Sale sessions that may stand as a charge: completed ones, and pending ones
     * whose answer the app never saw (the terminal approved while the app was
     * killed, then the cashier paid again). More than one is a possible double
     * charge nobody should reverse blindly — only a recorded failure is excluded.
     */
    possibleCharges(order, provider) {
        return this.sessionsFor(order, provider).filter(s => s.transaction_type === PaymentSession.SALE && s.status !== 'failed');
    },
    /**
     * The provider's reference to a completed sale AND the exact amount it
     * charged, from the locally stored session only — no backend lookup, because
     * the terminals operate mainly offline / in flight. The reference is whatever
     * the provider returned as `paymentRefundIdentificator`, recorded by
     * finalizeLocalSession. A sale that is not completed has no reference to trust.
     *
     * `saleAmount` is what was actually sent to the terminal. A void reverses the
     * original transaction, so the amount has to match it exactly — re-deriving
     * it from the order would reintroduce the sale/cancel mismatch (the sale
     * charges getCardAmount(), tip and business-card bucket included).
     */
    saleReference(sale) {
        const reference = sale?.status === 'completed' ? sale.response_data?.refund_reference : null;
        if (!sale || !reference)
            return null;
        if (!sale.amount || Money.parse(sale.amount).toUnit() <= 0)
            return null;
        return { reference: String(reference), saleSessionHash: sale.hash, saleAmount: sale.amount };
    },
    /**
     * Double-reversal guard: a completed void with no storno means a previous
     * cancel attempt died between the void and fiscalization — the retry must
     * skip the terminal and go straight to the storno.
     */
    findCompletedRefund(order) {
        return this.sessionsFor(order).find(s => s.transaction_type === PaymentSession.VOID && s.status === 'completed');
    },
    /**
     * A reversal whose terminal answer never arrived (the app was killed while
     * APOS held the void). Its outcome is unknown: a second void would be
     * declined by the terminal if the first went through, and would reverse the
     * card if it did not — so the cashier has to check the terminal journal
     * before this receipt can be cancelled again.
     */
    findPendingRefund(order) {
        return this.sessionsFor(order).find(s => s.transaction_type === PaymentSession.VOID && s.status === 'pending');
    },
    /**
     * Launch the terminal void for a full cancellation. The result arrives via
     * the cardReadCompleted event with intent 'cancelRefund'; the caller
     * registers its storno continuation before awaiting the event.
     */
    async startCancelRefund(order) {
        await this.initializeProvider();
        const provider = this.currentProvider;
        if (!provider || !CARD_PAYMENT_PROVIDERS[provider].supportsCancelReversal) {
            console.error(`${LOG_PREFIX} Cancel void: no provider with reversal support is initialized`);
            return { success: false, error: 'no_provider_configured' };
        }
        const charges = this.possibleCharges(order);
        if (charges.length > 1) {
            console.error(`${LOG_PREFIX} [${provider}] Cancel void blocked: ${charges.length} sale sessions completed or unanswered | order=${order.hash}`);
            return { success: false, error: 'refund_reference_ambiguous' };
        }
        const reference = this.saleReference(charges[0]);
        if (!reference) {
            console.error(`${LOG_PREFIX} [${provider}] Cancel void blocked: no local sale reference | order=${order.hash}`);
            return { success: false, error: 'refund_reference_missing' };
        }
        // The void must carry the amount the sale actually charged, not a re-derived
        // one — see saleReference. Formatting matches the sale exactly: the
        // session records 0.0000, the terminal receives 0.00.
        const cardAmount = Money.parse(reference.saleAmount);
        const amountStr = cardAmount.toFormat('0.0000');
        const currencyCode = this.getLocaleCurrency();
        console.log(`${LOG_PREFIX} [${provider}] Creating local void session | order=${order.hash} amount=${amountStr} reference=${reference.reference}`);
        const session = this.buildLocalSession(provider, order, PaymentSession.VOID, amountStr, {
            refund_of: reference.saleSessionHash,
            refund_reference: reference.reference,
        });
        await session.queue('create');
        session.announce();
        Store.set('pendingCardPayment', {
            provider,
            paymentSessionId: session.hash,
            order,
            printOptions: {},
            intent: 'cancelRefund',
        });
        console.log(`${LOG_PREFIX} [${provider}] Starting void card read | sessionId=${session.hash}`);
        this.terminal().startCardRead({
            provider,
            paymentSessionId: session.hash,
            // A negative amount is the native reversal trigger: the provider builds
            // a void against the referenced sale from it.
            amount: cardAmount.multiply(-1).toFormat('0.00'),
            currencyCode,
            paymentRefundIdentificator: reference.reference,
        });
        return { success: true };
    },
    /** ISO 4217 code the terminal charges in — the market's configured currency. */
    getLocaleCurrency() {
        return I18n.c('general.currency');
    },
    getCardAmount(order) {
        const payments = order.getPayments();
        const hasCardPayment = payments.some(p => p.key === PaymentTypeLabel.credit_card || p.key === PaymentTypeLabel.credit_card_business);
        if (!hasCardPayment)
            return 0;
        if (payments.length === 1 && payments[0].key === PaymentTypeLabel.credit_card) {
            return order.getPaidCreditCard().add(order.getTip()).toUnit();
        }
        return order.getPaidCreditCard().toUnit() || order.getPaidCreditCardBusiness().toUnit();
    }
};
export default CardReadService;
export { CardReadService };
//# sourceMappingURL=CardReadService.js.map